import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage } from 'services/apiError'
import CutileiLogo from 'icons/logo-name.png'
import PasswordInput from 'components/Inputs/PasswordInput'
import Business from 'components/Lists/LogInBusinessList/Business'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import submitOnEnter from 'utils/submitOnEnter'
import SelectBusinessModal from '../Modals/SelectBusinessModal'
import { MdNavigateBefore } from 'react-icons/md'
import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  Container,
  Form,
  Image,
  Title,
  LinkText,
  Label,
  Input,
  ButtonContainer,
  WarningText,
  ErrorContainer
} from './styles'

export default function SignIn () {
  const [loading, setLoading] = useState (false)
  const [businesses, setBusinesses] = useState ([])
  const [errorMessage, setErrorMessage] = useState (null)
  const [showBusinessesModal, setShowBusinessesModal] = useState (false)
  const navigate = useNavigate ()

  const signInValidation = Yup.object ({
    email: Yup
      .string ()
      .required ('É necessário fornecer um e-mail')
      .email ('Insira um email válido'),
  
    password: Yup
      .string ()
      .required ('É necessário fornecer uma senha')
      .min (8, 'A senha deve conter no minimo 8 caracteres')
  })

  const toggleBusinessesModal = () => setShowBusinessesModal (!showBusinessesModal)

  return (
    <Container>
      <Formik
        validationSchema={signInValidation}
        initialValues={{
          'email': '',
          'business': null,
          'professional': null,
          'password': ''
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setSubmitting (true)

            const signInValues = {
              businessId: values.business.id,
              professionalId: values.professional?.id ?? null,
              password: values.password
            }

            const { data } = await cutileiApi.post ('/auth/businesses', signInValues)

            setErrorMessage (null)
            AuthService.signIn (data)
            navigate ('/business', { replace: true })
          } catch (error) {
            setErrorMessage (getApiErrorMessage (error))
          } finally {
            setSubmitting (false)
          }
        }}
      >
        {function SignInForm ({
          values,
          errors,
          touched,
          isSubmitting,
          handleChange,
          setFieldValue,
          handleBlur,
          handleSubmit
        }) {
          const getBusiness = async () => {
            if (values.email && !errors.email) {
              try {
                setLoading (true)
                const { email } = values

                const { data: responseData } = await cutileiApi.get (
                  `/auth/businesses?email=${encodeURIComponent (email)}`
                )

                const { businesses, professional } = responseData
                setBusinesses (businesses)
                setFieldValue ('professional', professional ?? null)

                if (businesses.length === 1) {
                  setFieldValue ('business', businesses[0])
                } else {
                  toggleBusinessesModal ()
                }
              } catch (error) {
                setErrorMessage (getApiErrorMessage (error))
              } finally {
                setLoading (false)
              }
            }
          }

          const handleSelectBusiness = business => {
            setFieldValue ('business', business)
          }

          const goBack = () => {
            setFieldValue ('business', null)
            setErrorMessage (null)
          }

          const handleSignIn = values.business ? handleSubmit : getBusiness

          return (
            <Form onSubmit={handleSubmit} onKeyDown={submitOnEnter(handleSignIn, isSubmitting || loading)}>
              <Image src={CutileiLogo} alt='Cutilei'/>
              <Title>Entrar</Title>
              {!values.business && (
                <>
                  <Input
                    type='email'
                    placeholder='E-mail'
                    value={values.email}
                    onChange={handleChange ('email')}
                    onBlur={handleBlur ('email')}
                  />
                  {touched.email && errors.email && (
                    <WarningText>
                      {errors.email}
                    </WarningText>
                  )}
                  <SelectBusinessModal
                    visible={showBusinessesModal}
                    businesses={businesses}
                    onConfirm={handleSelectBusiness}
                    onClose={toggleBusinessesModal}
                  />
                </>
              )}
              {values.business && (
                <>
                  <ButtonContainer>
                    <SecondaryButton
                      label=''
                      onClick={goBack}
                      leftIcon={
                        <MdNavigateBefore
                          size={30}
                          color='#FFFFFF'
                          style={{marginTop: 4, marginRight: 2}}
                        />
                      }
                      disabled={loading}
                      loading={loading}
                      width={46}
                    />
                  </ButtonContainer>
                  <Business data={values.business} clickable={false}/>
                  <PasswordInput
                    placeholder='Senha'
                    value={values.password}
                    onChange={handleChange ('password')}
                    onBlur={handleBlur ('password')}
                    containerStyle={{marginTop: 10}}
                  />
                  {touched.password && errors.password && (
                    <WarningText>
                      {errors.password}
                    </WarningText>
                  )}
                  <LinkText to='/forgot-password' state={{ userType: 'business' }}>
                    Esqueceu sua senha?
                  </LinkText>
                </>
              )}
              {errorMessage && (
                <ErrorContainer>
                  <p>{errorMessage}</p>
                </ErrorContainer>
              )}
              <ButtonContainer>
                <MainButton
                  label='Entrar'
                  onClick={handleSignIn}
                  disabled={isSubmitting || loading}
                  loading={isSubmitting || loading}
                  width={140}
                />
              </ButtonContainer>
              <Label>Ainda não cadastrou seu salão?</Label>
              <SecondaryButton
                label='Cadastrar-se'
                onClick={() => navigate ('/signup')}
                width={140}
              />
            </Form>
          )
        }}
      </Formik>
    </Container>
  )
}
